import React, { useCallback, useEffect, useMemo, useState } from 'react';

import CSSModule from 'react-css-modules';
import { Search, UserPlus,X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { BusinessInfoTile, Spinner } from 'components';
import { actions } from 'coupon-common/src/modules/businesses/all';
import { actions as followActions } from 'coupon-common/src/modules/businesses/follow';

import { RouteNames } from '../../../constants';
import styles from './FollowedBusinesses.module.scss';

const FollowedBusinesses = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allState = useSelector(state => state.businesses.all);
  const followState = useSelector(state => state.businesses.follow);

  const { fetching, fetched, data, error } = allState;
  const { results: list, nextPage } = data || { results: [] };

  const [searchKey, setSearchKey] = useState('');

  const getAllList = useCallback(
    params => {
      dispatch(actions.getAllList(params));
    },
    [dispatch],
  );

  useEffect(() => {
    getAllList();
  }, [getAllList]);

  useEffect(() => {
    dispatch(followActions.getFollowedBusinessesRequest());
  }, [dispatch]);

  const goToDetails = id => navigate(`${RouteNames.BUSINESSES_DETAILS}/${id}`);

  const followedIds = useMemo(() => (followState?.data?.results || []).map(item => item.business_id), [followState]);

  const prunedList = useMemo(() => (list || []).filter(item => followedIds.includes(item.id)), [list, followedIds]);

  const filteredFollowedBusinesses = useMemo(() => {
    if (!searchKey.trim()) return prunedList;
    const lowerSearchKey = searchKey.toLowerCase();
    return prunedList.filter(business => business.name?.toLowerCase().includes(lowerSearchKey));
  }, [prunedList, searchKey]);

  const handleLoadMore = () => {
    if (nextPage) {
      getAllList({ page: nextPage });
    }
  };

  if (error) {
    return (
      <div styleName="page">
        <div styleName="wrap">
          <p styleName="state-message">Something went wrong loading your followed businesses.</p>
        </div>
      </div>
    );
  }

  if (!list || (fetching && !fetched)) {
    return (
      <div styleName="page">
        <div styleName="wrap">
          <div styleName="state-message">
            <Spinner show size="md" />
          </div>
        </div>
      </div>
    );
  }

  if (prunedList.length === 0) {
    return (
      <div styleName="page">
        <div styleName="wrap">
          <div styleName="empty-state">
            <UserPlus size={42} styleName="empty-icon" />
            <p styleName="empty-text">You currently don&rsquo;t follow any businesses</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div styleName="page">
      <div styleName="wrap">
        <div styleName="search-bar-wrap">
          <Search size={18} styleName="search-icon" />
          <input
            type="text"
            placeholder="Search followed businesses..."
            value={searchKey}
            onChange={e => setSearchKey(e.target.value)}
            styleName="search-input"
          />
          {searchKey.length > 0 && (
            <button type="button" styleName="clear-btn" onClick={() => setSearchKey('')} aria-label="Clear search">
              <X size={16} />
            </button>
          )}
        </div>

        <div styleName="list">
          {filteredFollowedBusinesses.map((item, index) => (
            <BusinessInfoTile key={item.id} first={index === 0} data={item} onPress={() => goToDetails(item.id)} />
          ))}
        </div>

        {nextPage && (
          <div styleName="load-more-row">
            <button type="button" styleName="load-more-btn" onClick={handleLoadMore} disabled={fetching}>
              {fetching ? <Spinner show size="sm" /> : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSSModule(FollowedBusinesses, styles, { allowMultiple: true });
